/**
 * Ringpuffer der letzten Fehler im Browser — für „Problem melden" (Baustein 17).
 *
 * Wer ein Problem meldet, weiß selten, was technisch schiefging. Der Browser
 * weiß es: Konsolenfehler, unbehandelte Ausnahmen, Abrufe mit 4xx/5xx. Die
 * letzten zehn davon gehen mit der Meldung mit — ohne Screenshot, ohne Nachfrage.
 *
 * 🔴 Datenschutz: Adressen werden ohne Query-String gemerkt (`?q=Müller` wäre
 * ein Kundenname im Ticket), Texte auf 300 Zeichen gekürzt. Den Rest schrubbt
 * der Server (E-Mail, Telefon, Adresse).
 *
 * Früh installieren (main.tsx, vor dem ersten Render) — sonst fehlen genau die
 * Fehler, die beim Laden passieren.
 */

export type FehlerEintrag = {
  art: "console" | "fehler" | "promise" | "fetch"
  text: string
  zeit: string
}

const MAX = 10
const MAX_TEXT = 300
const puffer: FehlerEintrag[] = []
let installiert = false

/** Pfad ohne Query/Fragment — bei absoluter Adresse auch ohne fremden Host. */
export function ohneQuery(url: string): string {
  try {
    const u = new URL(url, typeof location !== "undefined" ? location.origin : "http://x")
    const eigen = typeof location !== "undefined" && u.origin === location.origin
    return (eigen ? "" : u.origin) + u.pathname + (u.search ? "?…" : "")
  } catch {
    return url.split(/[?#]/)[0]
  }
}

function kuerze(text: string): string {
  // Adressen im Text ebenfalls ohne Query
  const t = text.replace(/(https?:\/\/[^\s?#"')]+|\/api\/[^\s?#"')]+)\?[^\s"')]*/g, "$1?…")
  return t.length > MAX_TEXT ? t.slice(0, MAX_TEXT) + "…" : t
}

export function merkeFehler(art: FehlerEintrag["art"], text: string): void {
  puffer.push({ art, text: kuerze(String(text || "")), zeit: new Date().toISOString() })
  while (puffer.length > MAX) puffer.shift()
}

export function letzteFehler(): FehlerEintrag[] {
  return puffer.slice()
}

/** Nur für Tests. */
export function _pufferLeeren(): void {
  puffer.length = 0
}

function alsText(a: unknown): string {
  if (a instanceof Error) return `${a.name}: ${a.message}`
  if (typeof a === "string") return a
  try { return JSON.stringify(a) } catch { return String(a) }
}

export function fehlerPufferInstallieren(win: Window & typeof globalThis = window): void {
  if (installiert) return
  installiert = true

  const origError = win.console.error.bind(win.console)
  win.console.error = (...args: unknown[]) => {
    try { merkeFehler("console", args.map(alsText).join(" ")) } catch { /* nie werfen */ }
    origError(...args)
  }

  win.addEventListener("error", (e: ErrorEvent) => {
    const ort = e.filename ? ` @ ${ohneQuery(e.filename)}:${e.lineno}` : ""
    merkeFehler("fehler", `${e.message}${ort}`)
  })
  win.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    merkeFehler("promise", alsText(e.reason))
  })

  const origFetch = win.fetch.bind(win)
  win.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url
    const methode = (init?.method || (typeof input === "object" && "method" in input ? input.method : "GET") || "GET").toUpperCase()
    try {
      const r = await origFetch(input, init)
      if (!r.ok) merkeFehler("fetch", `${methode} ${ohneQuery(url)} → HTTP ${r.status}`)
      return r
    } catch (err) {
      merkeFehler("fetch", `${methode} ${ohneQuery(url)} → ${alsText(err)}`)
      throw err
    }
  }
}
